import {
  sequencesStepsCreateMutation,
  sequencesStepsListQueryKey,
  sequencesStepsRetrieveOptions,
  sequencesStepsUpdateMutation
} from '@crm/api'
import { DynamicForm } from '@crm/features/campaigns/components'
import { StepType, WaitType } from '@crm/types'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Form, Input, notification, Radio, Select } from 'antd'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

type Props = {
  id?: number
  formId: string
  onCancel: () => void
}

export const StepsForm = ({ id, formId, onCancel }: Props) => {
  const { t } = useLocales()
  const { id: campaignId } = useParams()
  const [form] = Form.useForm()

  const { invalidateQueries } = useInvalidatePredicateQueries()

  const stepType = Form.useWatch('step_type', form)

  const { data } = useQuery({
    ...sequencesStepsRetrieveOptions({
      path: { id: Number(id), sequence_id: String(campaignId) }
    }),
    enabled: !!id
  })

  useEffect(() => {
    if (data) {
      form.setFieldsValue(data)
    }
  }, [data, form])

  const { mutate: create } = useMutation({
    ...sequencesStepsCreateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => onSuccessMutation()
  })

  const { mutate: edit } = useMutation({
    ...sequencesStepsUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => onSuccessMutation()
  })

  const onSuccessMutation = () => {
    invalidateQueries([
      sequencesStepsListQueryKey({
        path: { sequence_id: String(campaignId) }
      })
    ])
    onCancel()
    notification.success({
      message: t('success.success'),
      description: t('success.success_data_save')
    })
  }

  const onSubmit = async () => {
    const values = await form.validateFields()

    const payload = {
      ...values,
      sequenceId: campaignId,
      id,
      email_message:
        typeof values.email_message === 'string'
          ? values?.email_message
          : values?.email_message?.target?.targetElm?.value
    }

    return id
      ? edit({
          body: payload,
          path: { id: Number(id), sequence_id: String(campaignId) }
        })
      : create({ body: payload, path: { sequence_id: String(campaignId) } })
  }

  return (
    <Form name={formId} form={form} onFinish={onSubmit} layout="vertical">
      <Form.Item
        name="title"
        label={t('campaigns.name')}
        rules={[{ required: true, message: t('views.name_is_required') }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="step_type"
        label={t('campaigns.type')}
        initialValue={StepType.TASK}
      >
        <Radio.Group>
          {Object.values(StepType).map((type) => (
            <Radio value={type} key={type}>
              {t(`campaigns.${type}`)}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="wait_time"
        label={t('campaigns.wait_time')}
        rules={[{ required: true }]}
      >
        <Input type="number" />
      </Form.Item>

      <Form.Item
        name="wait_type"
        label={t('campaigns.wait_type')}
        rules={[{ required: true }]}
      >
        <Select
          placeholder={t('main.select')}
          options={Object.values(WaitType).map((item) => ({
            value: item,
            label: t(`campaigns.${item}`)
          }))}
        />
      </Form.Item>

      <Form.Item name="description" label={t('campaigns.description')}>
        <Input.TextArea rows={4} />
      </Form.Item>

      <DynamicForm step_type={stepType} form={form} />
    </Form>
  )
}
